import { useState, type FormEvent } from 'react';
import { copy, type Language, type Plan } from '../content';
import { submitSalesLead } from '../services/salesLead';
import { Icon } from './Icon';

type QuoteRequestFormProps = {
  language: Language;
  plans: Plan[];
  initialPlanId?: Plan['id'];
};

export function QuoteRequestForm({ language, plans, initialPlanId = 'professional' }: QuoteRequestFormProps) {
  const text = copy[language].form;
  const [planId, setPlanId] = useState<Plan['id']>(initialPlanId);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [resultMode, setResultMode] = useState<'api' | 'email'>('email');
  const selectedPlan = plans.find((plan) => plan.id === planId) || plans[0];

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    setStatus('sending');

    try {
      const result = await submitSalesLead({
        planId: selectedPlan.id,
        plan: selectedPlan.name,
        name: String(data.get('name') || ''),
        labName: String(data.get('labName') || ''),
        email: String(data.get('email') || ''),
        phone: String(data.get('phone') || ''),
        country: String(data.get('country') || ''),
        users: String(data.get('users') || ''),
        notes: String(data.get('notes') || ''),
        language,
        source: 'marketing-website',
      });
      setResultMode(result.mode);
      setStatus('success');
      if (result.mode === 'email') window.location.href = result.href;
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div className="form-result form-result--success" role="status">
        <span><Icon name="check" /></span>
        <h3>{text.successTitle}</h3>
        <p>{resultMode === 'api' ? text.successBodyApi : text.successBody}</p>
        <button className="button button--ghost" type="button" onClick={() => setStatus('idle')}>
          {language === 'ar' ? 'إرسال طلب آخر' : 'Send another request'}
        </button>
      </div>
    );
  }

  return (
    <form className="quote-form quote-form--page" onSubmit={handleSubmit}>
      <div className="plan-selector" role="radiogroup" aria-label={text.selectedPlan}>
        {plans.map((plan) => (
          <label className={plan.id === planId ? 'is-selected' : ''} key={plan.id}>
            <input
              type="radio"
              name="plan"
              value={plan.id}
              checked={plan.id === planId}
              onChange={() => setPlanId(plan.id)}
            />
            <span><strong>{plan.name}</strong><small>{plan.audience}</small></span>
            <i><Icon name="check" /></i>
          </label>
        ))}
      </div>

      <div className="form-grid">
        <label><span>{text.name}</span><input name="name" autoComplete="name" required minLength={2} /></label>
        <label><span>{text.lab}</span><input name="labName" autoComplete="organization" required minLength={2} /></label>
        <label><span>{text.email}</span><input name="email" type="email" autoComplete="email" required /></label>
        <label><span>{text.phone}</span><input name="phone" type="tel" autoComplete="tel" required minLength={7} /></label>
        <label><span>{text.country}</span><input name="country" autoComplete="country-name" required /></label>
        <label>
          <span>{text.users}</span>
          <select name="users" required defaultValue="">
            <option value="" disabled>{language === 'ar' ? 'اختر' : 'Select'}</option>
            <option value="1-3">1–3</option><option value="4-10">4–10</option>
            <option value="11-25">11–25</option><option value="26+">26+</option>
          </select>
        </label>
        <label className="form-grid__wide"><span>{text.notes}</span><textarea name="notes" rows={4} maxLength={800} /></label>
      </div>
      <p className="form-privacy"><Icon name="shield" />{text.privacy}</p>
      {status === 'error' && <p className="form-error" role="alert">{text.error}</p>}
      <button className="button button--primary button--wide" type="submit" disabled={status === 'sending'}>
        {status === 'sending' ? text.sending : text.submit}<Icon name="arrow" />
      </button>
    </form>
  );
}
